import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, CalendarDays, Send, ArrowUpRight } from "lucide-react";
import { EVENT, LOGO_URL } from "@/config";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! The Hope Restored Missions team will be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" data-testid="site-footer" className="relative overflow-hidden bg-[#062012] text-white">
      <div className="noise-overlay pointer-events-none absolute inset-0 opacity-20" />
      <div className="relative mx-auto max-w-7xl px-4 pb-12 pt-24 md:px-8 md:pt-32">
        <Reveal>
          <h2
            data-testid="footer-big-cta"
            className="font-heading text-[clamp(3.5rem,13vw,10rem)] font-black leading-[0.9] tracking-tighter"
          >
            LET'S RUN
            <br />
            <span className="text-stroke-light">FOR HOPE.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-14 border-t border-white/10 pt-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="2Run4Hope logo" className="h-14 w-14 rounded-full object-cover" />
                <div>
                  <p className="font-heading text-lg font-bold">2Run4Hope</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-hope-bright">
                    {EVENT.org}
                  </p>
                </div>
              </div>
              <div className="mt-8 space-y-3 text-sm text-white/70">
                <p className="flex items-center gap-2.5">
                  <CalendarDays className="h-4 w-4 shrink-0 text-hope-bright" />
                  {EVENT.date} · {EVENT.time}
                </p>
                <p className="flex items-start gap-2.5">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-hope-bright" />
                  {EVENT.venue}, {EVENT.address}
                </p>
              </div>
              <a
                href="#register-widget"
                onClick={scrollToId("#register-widget")}
                data-testid="footer-register-btn"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-hope-green px-7 py-3.5 font-heading text-base font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-hope-ink"
              >
                Register Now
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="mb-6 font-heading text-2xl font-bold" data-testid="contact-form-heading">
                Questions? Sponsorships? Say hello.
              </p>
              <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="contact-form">
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-white placeholder-white/40 outline-none transition-colors duration-200 focus:border-hope-bright"
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email"
                  className="rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-white placeholder-white/40 outline-none transition-colors duration-200 focus:border-hope-bright"
                />
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Your message"
                  className="resize-none rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm text-white placeholder-white/40 outline-none transition-colors duration-200 focus:border-hope-bright sm:col-span-2"
                />
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-heading text-base font-bold text-hope-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-hope-bright disabled:opacity-50 sm:col-span-2 sm:justify-self-start"
                >
                  {sending ? "Sending..." : "Send Message"}
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row">
          <p data-testid="footer-copyright">
            © 2026 {EVENT.org}. All rights reserved.
          </p>
          <p>Every mile means something.</p>
        </div>
      </div>
    </footer>
  );
};
